import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {
    ALLOWED_DISTANCE, DEFAULT_DISTANCE_VALUE,
    DEVICE_DETAILS_MODEL,
    STEPS_CREATE_DEVICE
} from "../Model/Model";
import {
    GetAccessType,
    GetDeviceType,
    GetListStatusZone
} from "../../../../reduxToolkit/EmployeeZones/EmployeeZonesApi";
import {
    GetAntiPassBackType,
    GetEscortMode,
    GetListStatusDevice
} from "../../../../reduxToolkit/Devices/DevicesApi";
import {GetAllZone} from "../../../../reduxToolkit/EmployeeOnBoarding/EmployeeOnBoardingApi";
import {DeleteItemsApi, GetAllMedia} from "../../../../reduxToolkit/Commons/CommonsApi";
import {getWorkStations} from "../../../../reduxToolkit/CompanyEmployees/CompanyEmployeesApi";
import {toast} from "react-toastify";
import routes from "../../../../Routes/routes";
import {errorMsg} from "../../../../Apis/functions";
import {TABLES} from "../../../../Apis/Tables";
import HandleChange from "../../../../Helpers/HandleChange";
import {isStepValid} from "../Model/DeviceValidator";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 * @function CommonDevicesApp
 * @description Main component for handling device creation and updates
 * @returns {Object} Contains all state and handler functions for the component
 */

/**
 * Custom hook used to centralize device creation and update logic.
 *
 * This hook manages:
 * - Device form state.
 * - Device catalogs.
 * - Device deletion.
 * - Step navigation and validation.
 * - Device access relationships.
 * - Distance configuration.
 * - API integrations.
 *
 * @function CommonDevicesApp
 *
 * @returns {Object} Hook response object.
 */
export const CommonDevicesApp =()=>{
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const {id} = useParams();
    const { zoneIdProps, zoneNameProps } = location.state || {};
    // use State hook for local state management
    const [deleteDeviceShow, setDeleteDeviceShow] = useState(false);
    const [selectDeviceForDelete, setSelectDeviceForDelete] = useState([]);
    const [deletedFlag, setDeletedFlag] = useState(false);
    const [data, setData] = useState({...DEVICE_DETAILS_MODEL});
    const [deviceCustomAccess , setDeviceCustomAccess] = useState([]);
    const [deviceDepartment , setDeviceDepartment] = useState([]);
    const [deviceCustomAccessTwoFa , setDeviceCustomAccessTwoFa] = useState([]);
    const [deviceDepartmentTwoFa , setDeviceDepartmentTwoFa] = useState([]);

    const [steps, setSteps] = useState([]); // State for step titles in the event creation process
    const [activeStep, setActiveStep] = useState(0); // State to track active step in the process
    const { handleChange } = HandleChange({ model: data, setModel: setData });
    const [modified, setModified] = useState(false);

    // 1FA
    const [selectForDeleteEmp1FA, setSelectForDeleteEmp1FA] = useState([]);
    const [selectForDeleteDepa1FA, setSelectForDeleteDepa1FA] = useState([]);

    // 2FA
    const [selectForDeleteEmp2FA, setSelectForDeleteEmp2FA] = useState([]);
    const [selectForDeleteDepa2FA, setSelectForDeleteDepa2FA] = useState([]);

    /**
     * Function for changing steps
     * @param step
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    const [deviceTypeDistance, setDeviceTypeDistance] = useState([ALLOWED_DISTANCE[data?.data?.deviceType]]);
    const [defaultDistance, setDefaultDistance] =useState([DEFAULT_DISTANCE_VALUE[data?.data?.deviceType]])
    const [emptyFields, setEmptyFields] = useState(false);

    useEffect(()=>{
        setDeviceTypeDistance(ALLOWED_DISTANCE[data?.data?.deviceType] || []);
        setDefaultDistance(DEFAULT_DISTANCE_VALUE[data?.data?.deviceType] || []);

        if(id === undefined){
            setEmptyFields(false)
            handleClearSteps();
        }
    },[data?.data?.deviceType])

    useEffect(() => {
        if (data?.security?.antiPassBackType === 1) {
            setData(prev => ({
                ...prev,
                security: {
                    ...prev.security,
                    antiPassBackTime: 0,
                    antiPassBackAlert: 1,
                }
            }));
        }
    }, [
        data?.security?.antiPassBackType,
        data?.data?.deviceType,
        data?.security?.requestSignatureAlways
    ]);

    // This section re-route us when a device is deleted
    useEffect(() => {
        if (deletedFlag) {
            zoneNameProps !== undefined
                ? navigate(routes.zone.details + zoneIdProps)
                : navigate(routes.devices.list);
        }
    }, [deletedFlag]);

    /**
     * @function handleError
     * @description Handles API errors and displays appropriate toast messages
     * @param {Error} error - Error object from API call
     */
    const handleError = (error) => {
        if (error.response) {
            const code = error.response.data?.code;
            if (code !== undefined && code !== null) {
                errorMsg(code);
            }
        }
        else if (error.request) {
            const code = error.request.data?.code;
            if (code !== undefined && code !== null) {
                errorMsg(code);
            }
        }
    };

    /**
     * @function deleteSelectedDevices
     * @description Handles device deletion
     * @param {Array} deleteItem - Array of device IDs to delete
     */
    const deleteSelectedDevices = (deleteItem) => {
        const body = deleteItem;

        if (body.length > 0) {
            dispatch(DeleteItemsApi({tableName: TABLES.DEVICES, body})).then(
                ({
                     payload: {
                         data: { success, code },
                     },
                 }) => {
                    if (success === true) {
                        setDeletedFlag(true);
                        setSelectDeviceForDelete([]);
                    } else {
                        errorMsg(code);
                    }
                },
            );
        }
    };

    const clearMap = {
        [STEPS_CREATE_DEVICE.DATA.INDEX]: () =>
            setData(prev => ({
                ...prev,
                data: { ...DEVICE_DETAILS_MODEL.data }
            })),

        [STEPS_CREATE_DEVICE.OPTIONS.INDEX]: () =>
            setData(prev => ({
                ...prev,
                options: { ...DEVICE_DETAILS_MODEL.options }
            })),

        [STEPS_CREATE_DEVICE.SECURITY.INDEX]: () =>
            setData(prev => ({
                ...prev,
                security: { ...DEVICE_DETAILS_MODEL.security }
            })),

        [STEPS_CREATE_DEVICE.AUTHENTICATION.INDEX]: () =>
            setData(prev => ({
                ...prev,
                authentication: { ...DEVICE_DETAILS_MODEL.authentication }
            })),

        [STEPS_CREATE_DEVICE.RELAY.INDEX]: () =>
            setData(prev => ({
                ...prev,
                relay: { ...DEVICE_DETAILS_MODEL.relay }
            })),

        [STEPS_CREATE_DEVICE.UNOFA.INDEX]: () =>
            setData(prev => ({
                ...prev,
                unoFa: { ...DEVICE_DETAILS_MODEL.unoFa }
            })),

        [STEPS_CREATE_DEVICE.DOSFA.INDEX]: () =>
            setData(prev => ({
                ...prev,
                dosFa: { ...DEVICE_DETAILS_MODEL.dosFa }
            })),
    };

    const handleClearSteps = (step) => {
        clearMap[step]?.();
    };

    const getCatalogs = () => {
        dispatch(GetListStatusZone());
        dispatch(GetListStatusDevice());
        dispatch(GetAccessType());
        dispatch(GetDeviceType());
        dispatch(GetAntiPassBackType());
        dispatch(GetEscortMode());
        dispatch(GetAllZone());
        dispatch(GetAllMedia());
        dispatch(getWorkStations());
    }

    useEffect(() => {
        getCatalogs()
    }, []);

    const handleValidateSteps = () => {

        const isValid = isStepValid(
            activeStep,
            {
                data: data.data,
                options: data.options,
                security: data.security,
                authentication: data.authentication,
                relay: data.relay
            }
        );

        if (!isValid) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return false;
        }

        setEmptyFields(false);
        return true;
    };

    return {
        data,
        t,
        handleChange,
        emptyFields,
        id,
        deleteDeviceShow,
        setDeleteDeviceShow,
        selectDeviceForDelete,
        setDeletedFlag,
        location,zoneIdProps,
        deviceTypeDistance,
        defaultDistance,

        deviceCustomAccess,
        deviceDepartment,
        deviceCustomAccessTwoFa,
        deviceDepartmentTwoFa,
        zoneNameProps,
        dispatch,
        navigate,
        setEmptyFields,
        setData,
        modified,

        steps,
        setSteps,
        activeStep,
        setActiveStep,
        setModified,

        handleError,
        handleStepChange,
        handleClearSteps,
        deleteSelectedDevices,
        handleValidateSteps,

        setDeviceCustomAccess,
        setDeviceDepartment,
        setDeviceCustomAccessTwoFa,
        setDeviceDepartmentTwoFa,

        selectForDeleteEmp1FA,
        setSelectForDeleteEmp1FA,
        selectForDeleteDepa1FA,
        setSelectForDeleteDepa1FA,

        selectForDeleteEmp2FA,
        setSelectForDeleteEmp2FA,
        selectForDeleteDepa2FA,
        setSelectForDeleteDepa2FA
    }
}
